<template>
  <div class="dlms-page">
    <div class="dlms-background" aria-hidden="true"></div>
    <div class="map-overlay-red-50"></div>

    <AppHeader title="DLMS" />

    <div class="widget-rail widget-rail-left">
      <div class="widget catalog-widget">
        <div class="widget-header">
          <span class="widget-title">Protocol Catalog</span>
          <span class="proto-tag">{{ filteredTasks.length }} tasks</span>
        </div>
        <div class="catalog-accordion custom-scrollbar" role="list" aria-label="DLMS protocol catalog">
          <section
            v-for="source in catalogSources"
            :key="source.id"
            :class="['source-group', { active: selectedSource === source.id }]"
            role="listitem"
          >
            <button
              :class="['source-button', { active: selectedSource === source.id }]"
              type="button"
              :aria-expanded="selectedSource === source.id ? 'true' : 'false'"
              @click="selectedSource = source.id"
            >
              <span class="source-dot" :style="{ background: source.color }"></span>
              <span>{{ source.label }}</span>
              <strong>{{ sourceTaskCount(source.id) }}</strong>
              <span class="source-caret">{{ selectedSource === source.id ? '-' : '+' }}</span>
            </button>

            <div v-if="selectedSource === source.id" class="source-panel">
              <article
                v-for="task in sourceTasks(source.id)"
                :key="task.taskKey"
                class="task-card"
                draggable="true"
                @dragstart="handleTaskDragStart(task, $event)"
                @dragend="handleTaskDragEnd"
              >
                <div class="task-card-top">
                  <span class="task-source" :style="{ color: sourceColor(task.source) }">
                    {{ sourceLabel(task.source) }}
                  </span>
                  <span class="task-time">{{ task.applicabilityLabel }}</span>
                </div>
                <h3>{{ task.name }}</h3>
                <p>{{ task.summary }}</p>
                <div class="task-meta">
                  <span>{{ task.kind.toUpperCase() }}</span>
                  <span>{{ task.availabilityLabel }}</span>
                  <span v-if="task.obis">{{ task.obis }}</span>
                  <span>{{ task.objectType }}</span>
                  <span>A{{ task.attribute }}</span>
                  <span v-if="task.cfRefs.length">{{ task.cfRefs.join(', ') }}</span>
                </div>
              </article>
            </div>
          </section>
        </div>
        <div class="widget-footer">
          <span class="footer-note">Drag blocks into scenario steps.</span>
        </div>
      </div>
    </div>

    <main class="dlms-workspace">
      <section class="saved-scenarios-panel" aria-label="Saved DLMS scenarios">
        <div class="scenario-grid-header">
          <div>
            <span class="device-type">Scenarios</span>
            <h2>Saved scenario grid</h2>
          </div>
          <div class="scenario-grid-actions">
            <span class="proto-tag">{{ savedScenarios.length }} saved</span>
            <button class="ghost-button" type="button" @click="createScenario">Add scenario</button>
          </div>
        </div>

        <div v-if="savedScenarios.length" class="scenario-grid">
          <article
            v-for="scenario in savedScenarios"
            :key="scenario.id"
            class="scenario-card"
          >
            <div class="scenario-card-top">
              <div>
                <span class="device-type">{{ targetLabel(scenario.target) }}</span>
                <h3>{{ scenario.name }}</h3>
              </div>
              <button class="ghost-button small-button" type="button" @click="openSavedScenario(scenario)">
                Open
              </button>
            </div>
            <div class="scenario-card-stats">
              <span>{{ scenario.stepCount }} steps</span>
              <span>{{ scenario.taskCount }} tasks</span>
              <span>{{ scenario.updatedAt }}</span>
            </div>
            <div class="scenario-card-steps custom-scrollbar">
              <div
                v-for="step in scenario.steps"
                :key="step.id"
                class="scenario-card-step"
              >
                <strong>{{ step.label }}</strong>
                <span>{{ stepTaskSummary(step) }}</span>
              </div>
            </div>
          </article>
        </div>

        <div v-else class="empty-scenario-grid">
          <span>No saved scenarios yet.</span>
          <button class="ghost-button" type="button" @click="createScenario">Add scenario</button>
        </div>
      </section>
    </main>

    <div
      v-if="showScenarioModal"
      class="scenario-modal-layer"
      aria-label="Scenario editor"
      role="dialog"
      aria-modal="false"
    >
      <section class="scenario-panel scenario-modal">
        <div class="scenario-header">
          <div>
            <span class="device-type">Scenario</span>
            <input
              v-model="scenarioName"
              class="scenario-name-input"
              type="text"
              aria-label="Scenario name"
              placeholder="Scenario name"
            />
          </div>
          <div class="target-selector" aria-label="Scenario device target">
            <button
              v-for="target in scenarioTargets"
              :key="target.id"
              :class="['target-button', { active: selectedScenarioTarget === target.id }]"
              type="button"
              @click="selectedScenarioTarget = target.id"
            >
              {{ target.label }}
            </button>
          </div>
          <div class="scenario-actions">
            <button class="ghost-button" type="button" @click="closeScenarioModal">Close</button>
            <button class="ghost-button" type="button" @click="clearPattern">Clear</button>
            <button class="ghost-button" type="button" @click="addScenarioStep">Add step</button>
            <button class="primary-button" type="button" @click="markDraftSaved">Save</button>
          </div>
        </div>

        <div class="scenario-steps custom-scrollbar">
          <div
            v-for="step in scenarioSteps"
            :key="step.id"
            :class="['scenario-step', { 'drop-active': activeDropStep === step.id }]"
            @dragover.prevent="handleScenarioStepDragOver(step.id)"
            @dragleave="handleScenarioStepDragLeave(step.id)"
            @drop.prevent="handleScenarioStepDrop(step.id, $event)"
          >
            <div class="scenario-step-copy">
              <div class="scenario-step-fields">
                <input
                  v-model="step.label"
                  class="step-title-input"
                  type="text"
                  aria-label="Step name"
                  placeholder="Step name"
                />
                <textarea
                  v-model="step.hint"
                  class="step-hint-input"
                  aria-label="Step description"
                  placeholder="Step description"
                  rows="2"
                ></textarea>
              </div>
              <button
                class="remove-step-button"
                type="button"
                aria-label="Remove step"
                :disabled="scenarioSteps.length <= 1"
                @click="removeScenarioStep(step.id)"
              >
                ×
              </button>
            </div>
            <div class="scenario-step-drop">
              <template v-if="step.tasks.length">
                <div
                  v-for="task in step.tasks"
                  :key="task.instanceId"
                  class="attached-task"
                >
                  <span class="attached-dot" :style="{ background: sourceColor(task.source) }"></span>
                  <span>{{ task.name }}</span>
                  <button type="button" aria-label="Remove task" @click.stop="removeScenarioTask(step.id, task.instanceId)">
                    ×
                  </button>
                </div>
              </template>
              <span v-else class="empty-drop">Drop task here</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import AppHeader from './AppHeader.vue';

const TASK_SOURCES = [
  { id: 'discovery', label: 'Discovery', color: '#5bb6ff' },
  { id: 'identity', label: 'Identity', color: '#8b7cff' },
  { id: 'clock', label: 'Clock', color: '#63e6be' },
  { id: 'communication', label: 'Communication', color: '#5bc8e0' },
  { id: 'archive', label: 'Archive', color: '#ff9d4d' },
  { id: 'diagnostic', label: 'Diagnostic', color: '#c084fc' },
  { id: 'battery', label: 'Battery', color: '#39d98a' },
  { id: 'security', label: 'Security', color: '#b8f2d2' },
  { id: 'compact-frame', label: 'Compact Frame', color: '#7bb3e0' },
  { id: 'firmware', label: 'Firmware', color: '#fb7185' }
];

const AVAILABILITY_LABELS = {
  protocolBaseline: 'Baseline',
  ifObjectPresent: 'Object present',
  accessDependent: 'Access rights',
  compactFrame: 'Compact frame',
  cfMapped: 'CF mapped'
};

const HIDDEN_TASK_GROUPS = new Set(['register', 'control']);

const dlmsTask = (
  id,
  source,
  name,
  summary,
  kind,
  objectType,
  attribute,
  availability,
  obis = '',
  dataType = '',
  cfRefs = []
) => ({
  id,
  source,
  name,
  summary,
  kind,
  objectType,
  attribute,
  availability,
  availabilityLabel: AVAILABILITY_LABELS[availability] || availability,
  obis,
  dataType,
  cfRefs
});

const withApplicability = (tasks, applicability, applicabilityLabel) =>
  tasks.map(task => ({
    ...task,
    taskKey: `${applicability}:${task.id}`,
    applicability,
    applicabilityLabel
  }));

const UDP_WRAPPER_TASK_LIBRARY = [
  dlmsTask('get_current_association_object_list', 'discovery', 'Current association object list', 'Read Association LN.object_list to discover real objects and access rights before scheduling meter-specific reads.', 'get', 'AssociationLogicalName', 2, 'protocolBaseline', '0.0.40.0.0.255'),
  dlmsTask('get_current_association_xdlms_context', 'discovery', 'Current association xDLMS context', 'Read negotiated xDLMS context information such as conformance and max PDU size.', 'get', 'AssociationLogicalName', 5, 'protocolBaseline', '0.0.40.0.0.255'),
  dlmsTask('get_current_association_status', 'discovery', 'Current association status', 'Read current association status.', 'get', 'AssociationLogicalName', 8, 'protocolBaseline', '0.0.40.0.0.255'),
  dlmsTask('get_logical_device_name', 'identity', 'COSEM logical device name', 'Read the logical device name when exposed by the meter object list.', 'get', 'Data', 2, 'ifObjectPresent', '0.0.42.0.0.255'),
  dlmsTask('get_sap_assignment_list', 'identity', 'SAP assignment list', 'Read logical-device SAP assignments when the SAP Assignment object is present.', 'get', 'SapAssignment', 2, 'ifObjectPresent', '0.0.41.0.0.255'),

  dlmsTask('get_clock_time', 'clock', 'Clock time', 'Read DLMS Clock.time.', 'get', 'Clock', 2, 'ifObjectPresent', '0.0.1.0.0.255'),
  dlmsTask('set_clock_time', 'clock', 'Set clock time', 'Set DLMS Clock.time when access rights allow clock synchronization.', 'set', 'Clock', 2, 'accessDependent', '0.0.1.0.0.255', 'OctetString'),
  dlmsTask('get_clock_timezone', 'clock', 'Clock time zone', 'Read DLMS Clock.time_zone.', 'get', 'Clock', 3, 'ifObjectPresent', '0.0.1.0.0.255'),
  dlmsTask('set_clock_timezone', 'clock', 'Set clock time zone', 'Set DLMS Clock.time_zone when access rights allow it.', 'set', 'Clock', 3, 'accessDependent', '0.0.1.0.0.255', 'Int16'),
  dlmsTask('get_clock_status', 'clock', 'Clock status', 'Read DLMS Clock.status.', 'get', 'Clock', 4, 'ifObjectPresent', '0.0.1.0.0.255'),
  dlmsTask('get_clock_daylight_savings_begin', 'clock', 'Clock daylight savings begin', 'Read DLMS Clock.daylight_savings_begin.', 'get', 'Clock', 5, 'ifObjectPresent', '0.0.1.0.0.255'),
  dlmsTask('get_clock_daylight_savings_end', 'clock', 'Clock daylight savings end', 'Read DLMS Clock.daylight_savings_end.', 'get', 'Clock', 6, 'ifObjectPresent', '0.0.1.0.0.255'),
  dlmsTask('get_clock_daylight_savings_enabled', 'clock', 'Clock daylight savings enabled', 'Read DLMS Clock.daylight_savings_enabled.', 'get', 'Clock', 8, 'ifObjectPresent', '0.0.1.0.0.255'),

  dlmsTask('get_tcp_udp_port', 'communication', 'TCP/UDP port', 'Read TCP-UDP Setup.port for wrapper/IP transports when the object is present.', 'get', 'TCPUDPSetup', 2, 'ifObjectPresent', '0.0.25.0.0.255'),
  dlmsTask('get_tcp_udp_ip_reference', 'communication', 'TCP/UDP IP reference', 'Read TCP-UDP Setup.IP_reference for wrapper/IP transports when the object is present.', 'get', 'TCPUDPSetup', 3, 'ifObjectPresent', '0.0.25.0.0.255'),
  dlmsTask('get_tcp_udp_inactivity_timeout', 'communication', 'TCP/UDP inactivity timeout', 'Read TCP-UDP Setup.inactivity_timeout for wrapper/IP transports when the object is present.', 'get', 'TCPUDPSetup', 6, 'ifObjectPresent', '0.0.25.0.0.255'),

  dlmsTask('get_archive_buffer_all', 'archive', 'Archive buffer', 'Read ProfileGeneric.buffer for a discovered archive/log profile object.', 'get', 'ProfileGeneric', 2, 'ifObjectPresent'),
  dlmsTask('get_archive_buffer_by_time_range', 'archive', 'Archive buffer from/to', 'Read ProfileGeneric.buffer with from/to selective access for discovered archive/log profile objects.', 'get', 'ProfileGeneric', 2, 'ifObjectPresent'),
  dlmsTask('get_archive_buffer_by_entry_range', 'archive', 'Archive buffer by entry range', 'Read ProfileGeneric.buffer with entry/index selective access for discovered archive/log profile objects.', 'get', 'ProfileGeneric', 2, 'ifObjectPresent'),
  dlmsTask('get_archive_capture_objects', 'archive', 'Archive capture objects', 'Read ProfileGeneric.capture_objects to decode archive rows and build range selectors.', 'get', 'ProfileGeneric', 3, 'ifObjectPresent'),
  dlmsTask('get_archive_capture_period', 'archive', 'Archive capture period', 'Read ProfileGeneric.capture_period when present.', 'get', 'ProfileGeneric', 4, 'ifObjectPresent'),
  dlmsTask('get_archive_entries_in_use', 'archive', 'Archive entries in use', 'Read ProfileGeneric.entries_in_use before requesting archive ranges.', 'get', 'ProfileGeneric', 7, 'ifObjectPresent'),
  dlmsTask('get_archive_profile_entries', 'archive', 'Archive capacity', 'Read ProfileGeneric.profile_entries to know archive capacity.', 'get', 'ProfileGeneric', 8, 'ifObjectPresent'),
  dlmsTask('get_profile_buffer_all', 'archive', 'Profile buffer', 'Read ProfileGeneric.buffer for a discovered profile object.', 'get', 'ProfileGeneric', 2, 'ifObjectPresent'),
  dlmsTask('get_profile_buffer_by_time_range', 'archive', 'Profile buffer by time range', 'Read ProfileGeneric.buffer with from/to selective access for archives and logs.', 'get', 'ProfileGeneric', 2, 'ifObjectPresent'),
  dlmsTask('get_profile_capture_objects', 'archive', 'Profile capture objects', 'Read ProfileGeneric.capture_objects to decode buffer rows and build range selectors.', 'get', 'ProfileGeneric', 3, 'ifObjectPresent'),
  dlmsTask('get_profile_capture_period', 'archive', 'Profile capture period', 'Read ProfileGeneric.capture_period when present.', 'get', 'ProfileGeneric', 4, 'ifObjectPresent'),
  dlmsTask('get_profile_entries_in_use', 'archive', 'Profile entries in use', 'Read ProfileGeneric.entries_in_use before requesting archive ranges.', 'get', 'ProfileGeneric', 7, 'ifObjectPresent'),
  dlmsTask('get_profile_profile_entries', 'archive', 'Profile capacity', 'Read ProfileGeneric.profile_entries to know archive capacity.', 'get', 'ProfileGeneric', 8, 'ifObjectPresent'),

  dlmsTask('get_gsm_operator', 'diagnostic', 'GSM operator', 'Read GSM Diagnostic.operator when the GSM Diagnostic object exists.', 'get', 'GSMDiagnostic', 2, 'ifObjectPresent'),
  dlmsTask('get_gsm_status', 'diagnostic', 'GSM status', 'Read GSM Diagnostic.status when the GSM Diagnostic object exists.', 'get', 'GSMDiagnostic', 3, 'ifObjectPresent'),
  dlmsTask('get_gsm_cell_info', 'diagnostic', 'GSM cell info', 'Read GSM Diagnostic.cell_info when the GSM Diagnostic object exists.', 'get', 'GSMDiagnostic', 6, 'ifObjectPresent'),
  dlmsTask('get_security_policy', 'security', 'Security policy', 'Read SecuritySetup.security_policy when the Security Setup object exists and access rights allow it.', 'get', 'SecuritySetup', 2, 'ifObjectPresent'),
  dlmsTask('get_security_suite', 'security', 'Security suite', 'Read SecuritySetup.security_suite when the Security Setup object exists and access rights allow it.', 'get', 'SecuritySetup', 3, 'ifObjectPresent'),
  dlmsTask('get_server_system_title', 'security', 'Server system title', 'Read SecuritySetup.server_system_title when exposed by the meter.', 'get', 'SecuritySetup', 5, 'ifObjectPresent'),

  dlmsTask('get_compact_data_buffer', 'compact-frame', 'Compact data buffer', 'Read CompactData.buffer when a compact data object is present; use this instead of redundant basic polling for devices already pushing CF data.', 'get', 'CompactData', 2, 'ifObjectPresent'),
  dlmsTask('get_compact_data_template_description', 'compact-frame', 'Compact data template description', 'Read CompactData.template_description to decode compact frames when available.', 'get', 'CompactData', 3, 'ifObjectPresent'),

  dlmsTask('get_image_transfer_status', 'firmware', 'Image transfer status', 'Read ImageTransfer.image_transfer_status when firmware transfer is supported.', 'get', 'ImageTransfer', 6, 'ifObjectPresent')
];

const SONATA_OCTAVE_TASK_LIBRARY = [
  dlmsTask('get_manufacturing_number', 'identity', 'Manufacturing number', 'Read stable device serial/manufacturing number.', 'get', 'Data', 2, 'cfMapped', '0.0.96.1.0.255', '', ['CF1']),
  dlmsTask('get_metering_point_identifier', 'identity', 'Metering point identifier', 'Read customer/site metering point identifier.', 'get', 'Data', 2, 'cfMapped', '0.0.96.1.10.255', '', ['CF1']),
  dlmsTask('get_equipment_class_identifier', 'identity', 'Equipment class identifier', 'Read meter class/model identifier.', 'get', 'Data', 2, 'cfMapped', '0.0.96.1.3.255', '', ['CF1']),
  dlmsTask('get_unis_reference_version', 'identity', 'UNI/TS reference version', 'Read supported UNI/TS data model version.', 'get', 'Data', 2, 'cfMapped', '0.0.96.1.5.255', '', ['CF1']),

  dlmsTask('get_clock_time', 'clock', 'Clock time', 'Read DLMS Clock.time.', 'get', 'Clock', 2, 'ifObjectPresent', '0.0.1.0.0.255'),
  dlmsTask('set_clock_time', 'clock', 'Set clock time', 'Set DLMS Clock.time.', 'set', 'Clock', 2, 'accessDependent', '0.0.1.0.0.255', 'OctetString'),
  dlmsTask('get_unix_time', 'clock', 'UNIX time', 'Read compact-data UNIX time object.', 'get', 'Data', 2, 'cfMapped', '0.0.1.1.0.255', '', ['CF47', 'CF48', 'CF49', 'CF73', 'CF74', 'CF75']),
  dlmsTask('set_unix_time', 'clock', 'Set UNIX time', 'Set compact-data UNIX time object.', 'set', 'Data', 2, 'accessDependent', '0.0.1.1.0.255', 'Uint32'),
  dlmsTask('get_clock_status', 'clock', 'Clock status', 'Read DLMS Clock.status.', 'get', 'Clock', 4, 'ifObjectPresent', '0.0.1.0.0.255'),
  dlmsTask('set_clock_timezone', 'clock', 'Set clock time zone', 'Set DLMS Clock.time_zone.', 'set', 'Clock', 3, 'accessDependent', '0.0.1.0.0.255', 'Int16'),

  dlmsTask('get_daily_load_profile_range_sonata', 'archive', 'Daily load profile range (Sonata A=7)', 'Read Daily Load Profile.buffer with from/to selective access.', 'get', 'ProfileGeneric', 2, 'cfMapped', '7.0.99.99.3.255', '', ['CF14', 'CF48', 'CF74']),
  dlmsTask('get_daily_load_profile_range_octave', 'archive', 'Daily load profile range (Octave A=8)', 'Read Daily Load Profile.buffer with from/to selective access.', 'get', 'ProfileGeneric', 2, 'cfMapped', '8.0.99.99.3.255', '', ['CF14', 'CF48', 'CF74']),
  dlmsTask('get_snapshot_period_data_range_sonata', 'archive', 'Snapshot period data range (Sonata A=7)', 'Read billing/snapshot archive with from/to selective access.', 'get', 'ProfileGeneric', 2, 'cfMapped', '7.0.98.11.0.255', '', ['CF16']),
  dlmsTask('get_snapshot_period_data_range_octave', 'archive', 'Snapshot period data range (Octave A=8)', 'Read billing/snapshot archive with from/to selective access.', 'get', 'ProfileGeneric', 2, 'cfMapped', '8.0.98.11.0.255', '', ['CF16']),
  dlmsTask('get_event_logbook_range_sonata', 'archive', 'Event logbook range (Sonata A=7)', 'Read Event Logbook.buffer with from/to selective access.', 'get', 'ProfileGeneric', 2, 'cfMapped', '7.0.99.98.0.255', '', ['CF15']),
  dlmsTask('get_event_logbook_range_octave', 'archive', 'Event logbook range (Octave A=8)', 'Read Event Logbook.buffer with from/to selective access.', 'get', 'ProfileGeneric', 2, 'cfMapped', '8.0.99.98.0.255', '', ['CF15']),
  dlmsTask('get_metrological_logbook_range_sonata', 'archive', 'Metrological logbook range (Sonata A=7)', 'Read Metrological Logbook.buffer with from/to selective access.', 'get', 'ProfileGeneric', 2, 'cfMapped', '7.0.99.98.1.255', '', ['CF15']),
  dlmsTask('get_metrological_logbook_range_octave', 'archive', 'Metrological logbook range (Octave A=8)', 'Read Metrological Logbook.buffer with from/to selective access.', 'get', 'ProfileGeneric', 2, 'cfMapped', '8.0.99.98.1.255', '', ['CF15']),
  dlmsTask('get_parameter_monitor_logbook_range_sonata', 'archive', 'Parameter monitor logbook range (Sonata A=7)', 'Read Parameter Monitor Logbook.buffer with from/to selective access.', 'get', 'ProfileGeneric', 2, 'cfMapped', '7.0.99.16.0.255', '', ['CF15']),
  dlmsTask('get_parameter_monitor_logbook_range_octave', 'archive', 'Parameter monitor logbook range (Octave A=8)', 'Read Parameter Monitor Logbook.buffer with from/to selective access.', 'get', 'ProfileGeneric', 2, 'cfMapped', '8.0.99.16.0.255', '', ['CF15']),

  dlmsTask('get_daily_diagnostic_sonata', 'diagnostic', 'Daily diagnostic (Sonata A=7)', 'Read daily diagnostic bit field.', 'get', 'Data', 2, 'cfMapped', '7.1.96.5.1.255', '', ['CF3', 'CF48', 'CF74']),
  dlmsTask('get_daily_diagnostic_octave', 'diagnostic', 'Daily diagnostic (Octave A=8)', 'Read daily diagnostic bit field.', 'get', 'Data', 2, 'cfMapped', '8.2.96.5.1.255', '', ['CF3', 'CF48', 'CF74']),
  dlmsTask('get_snapshot_period_diagnostic_sonata', 'diagnostic', 'Snapshot period diagnostic (Sonata A=7)', 'Read billing/snapshot diagnostic bit field.', 'get', 'Data', 2, 'cfMapped', '7.2.96.5.1.255', '', ['CF3', 'CF16']),
  dlmsTask('get_snapshot_period_diagnostic_octave', 'diagnostic', 'Snapshot period diagnostic (Octave A=8)', 'Read billing/snapshot diagnostic bit field.', 'get', 'Data', 2, 'cfMapped', '8.3.96.5.1.255', '', ['CF3', 'CF16']),
  dlmsTask('get_disconnect_output_state', 'diagnostic', 'Disconnect output state', 'Read valve/disconnect output state.', 'get', 'DisconnectControl', 2, 'cfMapped', '0.0.96.3.10.255', '', ['CF8']),
  dlmsTask('get_disconnect_control_state', 'diagnostic', 'Disconnect control state', 'Read valve/disconnect control state.', 'get', 'DisconnectControl', 3, 'cfMapped', '0.0.96.3.10.255', '', ['CF8']),

  dlmsTask('get_gsm_operator', 'communication', 'GSM operator', 'Read current network operator.', 'get', 'GSMDiagnostic', 2, 'cfMapped', '0.0.25.6.0.255', '', ['CF47', 'CF48', 'CF49', 'CF73', 'CF74', 'CF75']),
  dlmsTask('get_gsm_status', 'communication', 'GSM status', 'Read GSM/NB-IoT status.', 'get', 'GSMDiagnostic', 3, 'cfMapped', '0.0.25.6.0.255', '', ['CF47', 'CF48', 'CF49', 'CF73', 'CF74', 'CF75']),
  dlmsTask('get_gsm_cell_info', 'communication', 'GSM cell info', 'Read serving cell information.', 'get', 'GSMDiagnostic', 6, 'cfMapped', '0.0.25.6.0.255', '', ['CF47', 'CF48', 'CF49', 'CF73', 'CF74', 'CF75']),
  dlmsTask('get_pp4_network_status', 'communication', 'PP4 network status', 'Read PP4 network status.', 'get', 'Data', 2, 'cfMapped', '0.1.96.5.4.255', '', ['CF48', 'CF74']),
  dlmsTask('get_nbiot_timeout', 'communication', 'NB-IoT timeout', 'Read NB-IoT timeout settings.', 'get', 'Data', 2, 'ifObjectPresent', '0.1.94.39.52.255'),
  dlmsTask('set_nbiot_timeout', 'communication', 'Set NB-IoT timeout', 'Set NB-IoT timeout settings.', 'set', 'Data', 2, 'accessDependent', '0.1.94.39.52.255', 'Structure'),

  dlmsTask('get_battery_use_time', 'battery', 'Battery use time', 'Read battery use time counter.', 'get', 'Register', 2, 'cfMapped', '0.0.96.6.0.255', '', ['CF47', 'CF48', 'CF49', 'CF73', 'CF74', 'CF75']),
  dlmsTask('get_battery_estimated_remaining_time', 'battery', 'Battery estimated remaining time', 'Read estimated remaining battery use time.', 'get', 'Register', 2, 'cfMapped', '0.0.96.6.6.255', '', ['CF47', 'CF48', 'CF49', 'CF73', 'CF74', 'CF75']),

  dlmsTask('get_cf1_asset_data', 'compact-frame', 'CF1 asset data', 'Read compact asset data for unknown devices.', 'get', 'CompactData', 2, 'compactFrame', '0.0.66.0.1.255', '', ['CF1']),
  dlmsTask('get_cf3_diagnostics_alarms', 'compact-frame', 'CF3 diagnostics and alarms', 'Read compact diagnostics and alarms.', 'get', 'CompactData', 2, 'compactFrame', '0.0.66.0.3.255', '', ['CF3']),
  dlmsTask('get_cf14_daily_profile_response', 'compact-frame', 'CF14 daily profile response', 'Read compact daily archive response.', 'get', 'CompactData', 2, 'compactFrame', '0.0.66.0.14.255', '', ['CF14']),
  dlmsTask('get_cf15_event_response', 'compact-frame', 'CF15 event response', 'Read compact event/logbook response.', 'get', 'CompactData', 2, 'compactFrame', '0.0.66.0.15.255', '', ['CF15']),
  dlmsTask('get_cf16_billing_data', 'compact-frame', 'CF16 billing data', 'Read compact billing/snapshot response.', 'get', 'CompactData', 2, 'compactFrame', '0.0.66.0.16.255', '', ['CF16']),
  dlmsTask('get_cf48_content_b_pp4', 'compact-frame', 'CF48 content B PP4', 'Read compact PP4 content B.', 'get', 'CompactData', 2, 'compactFrame', '0.0.66.0.48.255', '', ['CF48']),
  dlmsTask('get_cf74_content_b_pp4', 'compact-frame', 'CF74 content B PP4', 'Read compact PP4 content B.', 'get', 'CompactData', 2, 'compactFrame', '0.0.66.0.74.255', '', ['CF74'])
];

const ALL_TASK_LIBRARY = [
  ...withApplicability(UDP_WRAPPER_TASK_LIBRARY, 'generic', 'Generic DLMS'),
  ...withApplicability(SONATA_OCTAVE_TASK_LIBRARY, 'sonata-octave', 'Sonata/Octave')
];

const SCENARIO_TARGETS = [
  { id: 'generic', label: 'Generic device' },
  { id: 'sonata', label: 'Sonata' },
  { id: 'octave', label: 'Octave' }
];

const createScenarioStep = () => ({
  id: `scenario-step-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  label: '',
  hint: '',
  tasks: []
});

const createScenarioSteps = () => [createScenarioStep()];

export default {
  name: 'DlmsTaskingPage',
  components: { AppHeader },
  data() {
    return {
      taskSources: TASK_SOURCES,
      scenarioTargets: SCENARIO_TARGETS,
      selectedSource: 'discovery',
      activeDropStep: null,
      scenarioName: '',
      selectedScenarioTarget: SCENARIO_TARGETS[0].id,
      customStepCount: 0,
      scenarioSteps: createScenarioSteps(),
      savedScenarios: [],
      showScenarioModal: false,
      editingScenarioId: null,
      draftSaved: false
    };
  },
  computed: {
    taskLibrary() {
      return ALL_TASK_LIBRARY.filter(task => !HIDDEN_TASK_GROUPS.has(task.source));
    },
    catalogSources() {
      return this.taskSources.filter(source => this.sourceTaskCount(source.id) > 0);
    },
    filteredTasks() {
      return this.taskLibrary.filter(task => task.source === this.selectedSource);
    },
    selectedSourceLabel() {
      return this.sourceLabel(this.selectedSource);
    }
  },
  methods: {
    sourceLabel(sourceId) {
      return this.taskSources.find(source => source.id === sourceId)?.label || sourceId;
    },
    sourceColor(sourceId) {
      return this.taskSources.find(source => source.id === sourceId)?.color || '#5bb6ff';
    },
    sourceTaskCount(sourceId) {
      return this.taskLibrary.filter(task => task.source === sourceId).length;
    },
    sourceTasks(sourceId) {
      return this.taskLibrary.filter(task => task.source === sourceId);
    },
    targetLabel(targetId) {
      return this.scenarioTargets.find(target => target.id === targetId)?.label || targetId;
    },
    cloneScenarioSteps(steps = this.scenarioSteps) {
      return steps.map(step => ({
        ...step,
        tasks: step.tasks.map(task => ({ ...task }))
      }));
    },
    scenarioTaskCount(steps = this.scenarioSteps) {
      return steps.reduce((total, step) => total + step.tasks.length, 0);
    },
    stepTaskSummary(step) {
      return step.tasks.length ? step.tasks.map(task => task.name).join(', ') : 'No tasks attached';
    },
    formatSavedTime(date = new Date()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    handleTaskDragStart(task, event) {
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('text/plain', task.taskKey);
      event.dataTransfer.setData('application/x-dlms-task', task.taskKey);
    },
    handleTaskDragEnd() {
      this.activeDropStep = null;
    },
    handleScenarioStepDragOver(stepId) {
      this.activeDropStep = stepId;
    },
    handleScenarioStepDragLeave(stepId) {
      if (this.activeDropStep === stepId) {
        this.activeDropStep = null;
      }
    },
    handleScenarioStepDrop(stepId, event) {
      const taskId =
        event.dataTransfer.getData('application/x-dlms-task') ||
        event.dataTransfer.getData('text/plain');

      this.attachTaskToScenarioStep(taskId, stepId);
      this.activeDropStep = null;
    },
    createTaskInstance(task, prefix) {
      return {
        ...task,
        instanceId: `${prefix}-${task.id}-${Date.now()}-${Math.random().toString(16).slice(2)}`
      };
    },
    attachTaskToScenarioStep(taskId, stepId) {
      const task = this.taskLibrary.find(item => item.taskKey === taskId || item.id === taskId);
      if (!task) return;

      const step = this.scenarioSteps.find(item => item.id === stepId);
      if (!step) return;

      step.tasks.push(this.createTaskInstance(task, stepId));
      this.draftSaved = false;
    },
    removeScenarioTask(stepId, instanceId) {
      const step = this.scenarioSteps.find(item => item.id === stepId);
      if (!step) return;

      step.tasks = step.tasks.filter(task => task.instanceId !== instanceId);
      this.draftSaved = false;
    },
    createScenario() {
      this.scenarioName = '';
      this.selectedScenarioTarget = SCENARIO_TARGETS[0].id;
      this.scenarioSteps = createScenarioSteps();
      this.editingScenarioId = null;
      this.showScenarioModal = true;
      this.draftSaved = false;
    },
    openSavedScenario(scenario) {
      this.scenarioName = scenario.name;
      this.selectedScenarioTarget = scenario.target;
      this.scenarioSteps = this.cloneScenarioSteps(scenario.steps);
      this.editingScenarioId = scenario.id;
      this.showScenarioModal = true;
      this.draftSaved = true;
    },
    closeScenarioModal() {
      this.showScenarioModal = false;
      this.activeDropStep = null;
    },
    addScenarioStep() {
      this.customStepCount += 1;
      this.scenarioSteps.push(createScenarioStep());
      this.draftSaved = false;
    },
    removeScenarioStep(stepId) {
      if (this.scenarioSteps.length <= 1) return;

      this.scenarioSteps = this.scenarioSteps.filter(step => step.id !== stepId);
      this.draftSaved = false;
    },
    clearPattern() {
      this.scenarioSteps = createScenarioSteps();
      this.draftSaved = false;
    },
    markDraftSaved() {
      const savedAt = new Date();
      const scenario = {
        id: this.editingScenarioId || `scenario-${savedAt.getTime()}`,
        name: this.scenarioName.trim() || 'Untitled scenario',
        target: this.selectedScenarioTarget,
        steps: this.cloneScenarioSteps(),
        stepCount: this.scenarioSteps.length,
        taskCount: this.scenarioTaskCount(),
        updatedAt: this.formatSavedTime(savedAt)
      };
      const existingIndex = this.savedScenarios.findIndex(item => item.id === scenario.id);

      if (existingIndex >= 0) {
        this.savedScenarios.splice(existingIndex, 1, scenario);
      } else {
        this.savedScenarios.unshift(scenario);
      }

      this.editingScenarioId = scenario.id;
      this.showScenarioModal = false;
      this.draftSaved = true;
    }
  }
};
</script>

<style scoped>
.dlms-page {
  min-height: 100vh;
  color: #fff;
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow: hidden;
}

.dlms-background {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
}

.map-overlay-red-50 {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(to bottom, rgba(5, 8, 16, 0.08), rgba(2, 4, 10, 0.42));
  mix-blend-mode: screen;
}

.widget-rail {
  position: fixed;
  top: 72px;
  z-index: 100;
  width: 320px;
  gap: 20px;
  display: flex;
  pointer-events: none;
  flex-direction: column;
}

.widget-rail > * {
  pointer-events: all;
}

.widget-rail-left {
  left: 50px;
}

.widget,
.scenario-panel,
.canvas-toolbar {
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.38), inset 0 1px 0 rgba(255,255,255,0.06);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
}

.widget {
  overflow: hidden;
  border-radius: 18px;
}

.catalog-widget {
  height: calc(100vh - 112px);
  display: flex;
  flex-direction: column;
}

.widget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
}

.widget-title {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.96);
  letter-spacing: 0.01em;
}

.widget-content {
  padding: 0 16px 12px;
}

.widget-kpi {
  display: flex;
  gap: 6px;
  padding: 8px 16px 12px;
  align-items: baseline;
}

.kpi-value {
  font-size: 32px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.96);
}

.kpi-unit {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.44);
}

.proto-tag {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5bb6ff;
  background: rgba(91, 182, 255, 0.12);
  border: 1px solid rgba(91, 182, 255, 0.25);
  white-space: nowrap;
}

.catalog-accordion {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 10px 14px 16px;
}

.source-group {
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.025);
}

.source-panel {
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 0 0 9px;
}

.source-button {
  width: 100%;
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 9px;
  min-height: 34px;
  padding: 8px 9px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0);
  color: rgba(255, 255, 255, 0.45);
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 650;
  text-align: left;
  transition: all 0.16s ease;
}

.source-button strong {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.36);
}

.source-caret {
  min-width: 10px;
  text-align: center;
  font-size: 13px;
  font-weight: 750;
  color: rgba(255, 255, 255, 0.34);
}

.source-button:hover,
.source-button.active {
  color: rgba(255, 255, 255, 0.92);
  background: rgba(255, 255, 255, 0.07);
}

.source-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
}

.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(91, 182, 255, 0.45) rgba(255, 255, 255, 0.06);
}

.custom-scrollbar::-webkit-scrollbar,
.saved-scenarios-panel::-webkit-scrollbar,
.scenario-steps::-webkit-scrollbar {
  width: 9px;
}

.custom-scrollbar::-webkit-scrollbar-track,
.saved-scenarios-panel::-webkit-scrollbar-track,
.scenario-steps::-webkit-scrollbar-track {
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.055);
}

.custom-scrollbar::-webkit-scrollbar-thumb,
.saved-scenarios-panel::-webkit-scrollbar-thumb,
.scenario-steps::-webkit-scrollbar-thumb {
  min-height: 60px;
  border: 2px solid rgba(8, 12, 22, 0.86);
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(91, 182, 255, 0.92), rgba(99, 230, 190, 0.72));
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover,
.saved-scenarios-panel::-webkit-scrollbar-thumb:hover,
.scenario-steps::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, rgba(116, 199, 255, 1), rgba(109, 240, 202, 0.92));
}

.task-card {
  padding: 11px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.045);
  cursor: grab;
  transition: transform 0.16s ease, background 0.16s ease, border-color 0.16s ease;
}

.task-card:hover {
  transform: translateY(-1px);
  border-color: rgba(91, 182, 255, 0.26);
  background: rgba(91, 182, 255, 0.08);
}

.task-card:active {
  cursor: grabbing;
}

.task-card-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 7px;
}

.task-source,
.task-time {
  font-size: 9px;
  font-weight: 750;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.task-time {
  color: rgba(255, 255, 255, 0.32);
}

.task-card h3 {
  margin: 0 0 5px;
  font-size: 12px;
  line-height: 1.22;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.92);
}

.task-card p {
  margin: 0;
  font-size: 11px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.46);
}

.task-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 9px;
}

.task-meta span {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 3px 6px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 750;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.48);
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.07);
}

.dlms-workspace {
  position: relative;
  z-index: 10;
  min-width: 0;
  margin: 4px 50px 0 390px;
  padding: 0 0 40px;
}

.canvas-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  min-height: 94px;
  margin-top: 8px;
  padding: 18px 20px;
  border-radius: 18px;
}

.canvas-toolbar h1 {
  margin: 0 0 6px;
  font-size: 22px;
  line-height: 1.1;
  font-weight: 750;
  letter-spacing: -0.02em;
}

.canvas-toolbar p {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.48);
}

.toolbar-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.ghost-button,
.primary-button {
  min-height: 36px;
  border-radius: 10px;
  padding: 0 14px;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 750;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  transition: all 0.16s ease;
}

.ghost-button {
  color: rgba(255, 255, 255, 0.62);
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.10);
}

.primary-button {
  color: rgba(3, 6, 13, 0.92);
  background: linear-gradient(135deg, #5bb6ff, #63e6be);
  border: 1px solid rgba(255, 255, 255, 0.10);
  box-shadow: 0 10px 24px rgba(91, 182, 255, 0.22);
}

.ghost-button:hover,
.primary-button:hover {
  transform: translateY(-1px);
}

.saved-scenarios-panel {
  max-height: calc(100vh - 196px);
  margin: 34px 0 16px;
  padding: 0;
  overflow-y: auto;
  color: rgba(255, 255, 255, 0.96);
  scrollbar-width: thin;
  scrollbar-color: rgba(91, 182, 255, 0.45) rgba(255, 255, 255, 0.06);
}

.scenario-grid-header,
.scenario-card-top,
.scenario-card-stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.scenario-grid-header {
  margin-bottom: 20px;
}

.scenario-grid-header h2 {
  margin: 0;
  font-size: 20px;
  line-height: 1.15;
  font-weight: 750;
  letter-spacing: -0.02em;
}

.scenario-grid-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.scenario-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.scenario-card {
  min-height: 230px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.10);
  background: rgba(255, 255, 255, 0.04);
}

.scenario-card h3 {
  margin: 0;
  font-size: 17px;
  line-height: 1.2;
  font-weight: 780;
  color: rgba(255, 255, 255, 0.92);
}

.small-button {
  min-height: 30px;
  padding: 0 10px;
  font-size: 10px;
}

.scenario-card-stats {
  justify-content: flex-start;
  flex-wrap: wrap;
}

.scenario-card-stats span {
  padding: 4px 7px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 750;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.52);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.scenario-card-steps {
  display: flex;
  flex-direction: column;
  gap: 7px;
  max-height: 126px;
  overflow-y: auto;
  padding-right: 4px;
}

.scenario-card-step {
  display: grid;
  gap: 3px;
  padding: 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.scenario-card-step strong {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.78);
}

.scenario-card-step span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.42);
}

.empty-scenario-grid {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.empty-scenario-grid span {
  font-size: 12px;
  font-weight: 650;
  color: rgba(255, 255, 255, 0.42);
}

.scenario-modal-layer {
  position: fixed;
  inset: 0 0 0 360px;
  z-index: 180;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 104px 48px 36px;
  pointer-events: none;
}

.scenario-panel {
  margin: 16px 0;
  padding: 16px;
  border-radius: 18px;
}

.scenario-modal {
  width: min(1120px, 100%);
  max-height: calc(100vh - 140px);
  display: flex;
  flex-direction: column;
  margin: 0;
  pointer-events: all;
}

.scenario-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
}

.scenario-name-input {
  width: min(360px, 100%);
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
  background: transparent;
  color: rgba(255, 255, 255, 0.94);
  font-family: 'Inter', sans-serif;
  font-size: 22px;
  line-height: 1.2;
  font-weight: 750;
  letter-spacing: -0.02em;
  outline: none;
  padding: 3px 0 6px;
}

.scenario-name-input:focus {
  border-bottom-color: rgba(91, 182, 255, 0.55);
}

.scenario-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.target-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  max-width: 260px;
  padding: 2px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.045);
}

.target-button {
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  color: rgba(255, 255, 255, 0.42);
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition: all 0.16s ease;
}

.target-button:hover,
.target-button.active {
  color: rgba(255, 255, 255, 0.92);
  background: rgba(255, 255, 255, 0.10);
}

.scenario-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
}

.scenario-step {
  min-height: 156px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.10);
  background: rgba(255, 255, 255, 0.04);
  transition: border-color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease;
}

.scenario-step.drop-active {
  border-color: rgba(91, 182, 255, 0.42);
  background: rgba(91, 182, 255, 0.08);
  box-shadow: inset 0 0 0 1px rgba(91, 182, 255, 0.20);
}

.scenario-step-copy {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.scenario-step-fields {
  flex: 1;
  min-width: 0;
}

.step-title-input,
.step-hint-input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.9);
  font-family: 'Inter', sans-serif;
}

.step-title-input {
  font-size: 12px;
  line-height: 1.3;
  font-weight: 800;
}

.step-hint-input {
  min-height: 34px;
  margin-top: 4px;
  resize: none;
  font-size: 10px;
  line-height: 1.35;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.46);
}

.step-title-input::placeholder,
.step-hint-input::placeholder {
  color: rgba(255, 255, 255, 0.28);
}

.step-title-input:focus,
.step-hint-input:focus {
  color: rgba(255, 255, 255, 0.96);
}

.remove-step-button {
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.055);
  color: rgba(255, 255, 255, 0.48);
  font-size: 15px;
  line-height: 20px;
  transition: all 0.16s ease;
}

.remove-step-button:hover:not(:disabled) {
  color: rgba(255, 255, 255, 0.92);
  border-color: rgba(255, 91, 110, 0.34);
  background: rgba(255, 91, 110, 0.18);
}

.remove-step-button:disabled {
  cursor: not-allowed;
  opacity: 0.32;
}

.scenario-step-drop {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 7px;
  min-height: 72px;
  padding: 8px;
  border-radius: 11px;
  border: 1px dashed rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.035);
}

.device-type {
  display: block;
  margin-bottom: 4px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.31);
}

.empty-drop {
  text-align: center;
  font-size: 11px;
  font-weight: 650;
  color: rgba(255, 255, 255, 0.32);
}

.attached-task {
  display: grid;
  grid-template-columns: 7px minmax(0, 1fr) 20px;
  align-items: center;
  gap: 7px;
  min-height: 28px;
  padding: 5px 6px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.07);
  font-size: 10.5px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.78);
}

.attached-task span:nth-child(2) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attached-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
}

.attached-task button {
  border: none;
  width: 20px;
  height: 20px;
  cursor: pointer;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.46);
  font-size: 14px;
  line-height: 18px;
}

.attached-task button:hover {
  color: rgba(255, 255, 255, 0.92);
  background: rgba(255, 91, 110, 0.18);
}

.meta-row {
  display: flex;
  font-size: 12px;
  margin-bottom: 8px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.meta-label {
  color: rgba(255, 255, 255, 0.44);
}

.meta-value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.68);
}

.status-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.badge-warn {
  color: #ff9d4d;
  background: rgba(255, 157, 77, 0.15);
  border: 1px solid rgba(255, 157, 77, 0.3);
}

.badge-good {
  color: #63e6be;
  background: rgba(99, 230, 190, 0.12);
  border: 1px solid rgba(99, 230, 190, 0.26);
}

.widget-footer {
  padding: 8px 16px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.footer-note {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.28);
}

@media (max-width: 1280px) {
  .widget-rail {
    width: 292px;
  }

  .widget-rail-left {
    left: 28px;
  }

  .dlms-workspace {
    margin-left: 344px;
    margin-right: 28px;
  }

  .scenario-modal-layer {
    left: 320px;
    padding-right: 28px;
  }
}

@media (max-width: 1040px) {
  .dlms-page {
    overflow-y: auto;
  }

  .widget-rail,
  .dlms-workspace {
    position: relative;
    top: auto;
    left: auto;
    right: auto;
    width: auto;
    min-width: 0;
    margin: 18px 24px;
  }

  .widget-rail {
    z-index: 10;
    display: block;
  }

  .catalog-widget,
  .catalog-accordion {
    max-height: none;
    height: auto;
  }

  .catalog-accordion {
    max-height: 460px;
  }

  .saved-scenarios-panel {
    max-height: none;
  }

  .scenario-modal-layer {
    inset: 0;
    padding: 96px 24px 32px;
    background: rgba(3, 6, 13, 0.64);
  }

}

@media (max-width: 720px) {
  .canvas-toolbar,
  .widget-rail,
  .scenario-steps {
    grid-template-columns: 1fr;
  }

  .canvas-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .scenario-header {
    flex-direction: column;
  }

  .scenario-modal-layer {
    padding: 88px 14px 24px;
  }

  .scenario-modal {
    max-height: calc(100vh - 112px);
  }

  .target-selector {
    justify-content: flex-start;
  }
}
</style>
